import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Radar, FileSearch, Scale, Check, Crown } from "lucide-react";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { useArticlePath } from "@/lib/i18n";
import { track } from "./navConfig";
import { Micro, PanelCTA, GhostWord, FOCUS, REGION, delay } from "./primitives";

const BENEFITS = [
  "Exclusive analysis & premium deep dives",
  "Regulation Radar — every Monday",
  "Completely ad-free reading",
  "Support an independent newsroom",
];

const PremiumRow = ({ to, icon: Icon, label, desc, badge, testid }: {
  to: string; icon: any; label: string; desc: string; badge?: string; testid: string;
}) => (
  <Link to={to} data-testid={testid} onClick={() => track("nav_click", { to })}
    className={`group/row flex items-start gap-3.5 rounded-lg border border-transparent px-2.5 py-3 -mx-2.5 hover:bg-[#7C3AED]/[0.07] hover:border-[#7C3AED]/25 transition-colors duration-200 ${FOCUS}`}>
    <span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center text-gray-500 group-hover/row:text-[#A78BFA] group-hover/row:border-[#7C3AED]/40 group-hover/row:bg-[#7C3AED]/10 transition-colors">
      <Icon className="h-4 w-4" />
    </span>
    <span className="min-w-0">
      <span className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-200 group-hover/row:text-white transition-colors">{label}</span>
        {badge && (
          <span className="font-mono text-[8px] font-bold uppercase tracking-widest text-[#A78BFA] border border-[#7C3AED]/40 rounded px-1.5 py-px">
            {badge}
          </span>
        )}
      </span>
      <span className="block text-xs text-gray-500 leading-snug mt-0.5">{desc}</span>
    </span>
  </Link>
);

export const PremiumMenu = () => {
  const { user } = useAuth();
  const toArticle = useArticlePath();
  const { data } = useQuery({
    queryKey: ["nav-deepdive"],
    queryFn: async () => (await api.get("/articles", { params: { content_type: "deep_dive", limit: 1 } })).data,
    staleTime: 5 * 60 * 1000,
  });
  const feature: Article | undefined = data?.items?.[0];
  const isMember = !!user?.is_premium;

  return (
    <div data-testid="layernav-premium" className="relative grid grid-cols-2 xl:grid-cols-[1.25fr_0.9fr_0.95fr] gap-7 p-7">
      <GhostWord word="Premium" />
      <div className={`col-span-2 xl:col-span-1 ${REGION}`} style={delay(0)}>
        <Micro className="mb-3.5">Editorial Spotlight</Micro>
        {feature ? (
          <Link to={toArticle(feature.slug)} data-testid="premium-featured-story"
            onClick={() => track("nav_click", { premium_feature: feature.slug })}
            className={`group/feat relative block rounded-xl overflow-hidden border border-[#7C3AED]/30 min-h-[240px] h-[calc(100%-30px)] ${FOCUS}`}>
            {feature.hero_image_url && (
              <img src={feature.hero_image_url} alt={feature.title} loading="lazy"
                className="nl-settle absolute inset-0 h-full w-full object-cover opacity-70 motion-safe:transition-transform motion-safe:duration-300 group-hover/feat:scale-[1.02]" />
            )}
            <span className="absolute inset-0 bg-gradient-to-t from-[#07080c] via-[#07080c]/50 to-transparent" />
            <span className="absolute inset-0 bg-[#7C3AED]/[0.14] mix-blend-screen" />
            <span className="relative z-10 flex flex-col justify-end h-full p-5">
              <span className="self-start font-mono text-[9px] uppercase tracking-[0.25em] text-[#A78BFA] border border-[#7C3AED]/50 rounded px-2 py-0.5 bg-black/40">
                Featured Story
              </span>
              <span className="font-news font-black text-white text-xl leading-tight mt-3 line-clamp-3 group-hover/feat:text-[#E9D5FF] transition-colors">
                {feature.title}
              </span>
              <span className="text-[13px] text-gray-400 mt-1.5 line-clamp-2">
                {feature.summary_ai || feature.summary}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#00E5FF] mt-3">
                Read Story <span aria-hidden className="motion-safe:transition-transform motion-safe:duration-200 group-hover/feat:translate-x-1">→</span>
              </span>
            </span>
          </Link>
        ) : (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 min-h-[240px] flex flex-col justify-end">
            <p className="font-news font-bold text-white text-lg">Premium deep dives, daily.</p>
            <p className="text-sm text-gray-500 mt-1">Original market intelligence from the NewsLayer desk.</p>
            <div className="mt-3"><PanelCTA to="/premium" testid="premium-featured-fallback">Explore Premium</PanelCTA></div>
          </div>
        )}
      </div>

      <div className={REGION} style={delay(1)}>
        <Micro cyan className="mb-3.5">Premium Content</Micro>
        <div className="space-y-1.5">
          <PremiumRow to="/radar" icon={Radar} label="Regulation Radar" badge="Weekly"
            desc="The premium policy letter — what regulators moved on and what it means" testid="premium-nav-radar" />
          <PremiumRow to="/research" icon={FileSearch} label="Premium Research"
            desc="Deep analysis and long-form intelligence from the NewsLayer desk" testid="premium-nav-research" />
          <PremiumRow to="/regulation-tracker" icon={Scale} label="Regulation Tracker"
            desc="Every bill, ruling and enforcement move — tracked as it happens" testid="premium-nav-tracker" />
        </div>
        <div className="border-t border-white/[0.07] mt-5 pt-4">
          <PanelCTA to="/premium" testid="premium-view-plans">Plans & pricing</PanelCTA>
        </div>
      </div>

      <div className={`relative overflow-hidden rounded-xl border border-[#7C3AED]/35 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] ${REGION}`}
        style={delay(2)} data-testid="premium-benefits">
        <span aria-hidden className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-48 w-64"
          style={{ background: "radial-gradient(closest-side, rgba(124,58,237,0.28), transparent)" }} />
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#A78BFA]/60 to-transparent" />
        <div className="relative">
          <Micro className="mb-2.5">Member Benefits</Micro>
          <p className="font-heading font-bold text-white text-[15px] leading-snug">
            Unlock the full NewsLayer advantage
          </p>
          <ul className="mt-3.5 space-y-2.5">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-[13px] text-gray-300">
                <Check className="h-3.5 w-3.5 mt-0.5 text-[#A78BFA] shrink-0" /> {b}
              </li>
            ))}
          </ul>
          {isMember ? (
            <Link to="/radar" data-testid="premium-member-cta" onClick={() => track("nav_click", { premium_cta: "open" })}
              className={`mt-5 flex items-center justify-center gap-2 rounded-md bg-[#7C3AED]/20 border border-[#7C3AED]/50 text-white font-bold text-sm py-2.5 hover:bg-[#7C3AED]/30 transition-colors ${FOCUS}`}>
              <Crown className="h-4 w-4 text-[#F59E0B]" /> Open Premium →
            </Link>
          ) : (
            <>
              <Link to="/premium" data-testid="premium-become-member" onClick={() => track("nav_click", { premium_cta: "join" })}
                className={`sparkle-btn mt-5 flex items-center justify-center rounded-md bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white font-bold text-sm py-2.5 shadow-[0_0_24px_rgba(124,58,237,0.35)] hover:from-[#9D6FFF] hover:to-[#7C3AED] hover:shadow-[0_0_32px_rgba(124,58,237,0.5)] transition-all duration-200 ${FOCUS}`}>
                Become a Member →
              </Link>
              {!user && (
                <Link to="/login" data-testid="premium-signin"
                  className={`block text-center text-xs text-gray-500 hover:text-white mt-2.5 transition-colors rounded ${FOCUS}`}>
                  Already a member? Sign in
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
